/* Prompt shown on/after a bill lock's unlock day: release sealed money to Spending. */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Unlock, Receipt } from "lucide-react";

const dueLabel = (days) => days === 0 ? "due today" : days === 1 ? "due tomorrow" : days < 0 ? `was due ${-days} day${days === -1 ? "" : "s"} ago` : `due in ${days} days`;

export default function BillLockReadyBanner() {
  const [ready, setReady] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bill-locks");
      setReady((data.bill_locks || []).filter((b) => b.phase === "ready"));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const release = async (b) => {
    setBusy(b.id); setError("");
    try {
      await api.post(`/bill-locks/${b.id}/release`);
      await load();
      window.dispatchEvent(new Event("bill-locks:refresh"));
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  if (ready.length === 0) return null;
  return (
    <div className="rounded-2xl bg-forest text-white p-5 sm:p-6 mb-4 relative overflow-hidden" data-testid="bill-lock-ready-banner">
      <div className="absolute inset-0 bg-gradient-to-br from-gold/15 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E8D8B5]">
          <Receipt className="w-3 h-3" strokeWidth={2} /> Bill lock unlocked
        </div>
        <div className="mt-2 space-y-2">
          {ready.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`bill-lock-ready-${b.id}`}>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl leading-tight">
                  {b.name} {b.days_until_due < 0 ? "" : "is "}{dueLabel(b.days_until_due)} — release {fmtMoney(b.locked_amount)} to Spending
                </h3>
                <p className="text-xs text-mint-mid mt-0.5">Sealed since {new Date(b.created_at).toLocaleDateString()} · unlocked {b.unlock_date}</p>
              </div>
              <button onClick={() => release(b)} disabled={busy === b.id}
                className="bg-cream text-ink rounded-lg px-4 py-2 text-sm font-medium hover:bg-white disabled:opacity-60 inline-flex items-center gap-1.5 transition-colors"
                data-testid={`bill-lock-banner-release-${b.id}`}>
                <Unlock className="w-3.5 h-3.5" strokeWidth={2} /> {busy === b.id ? "Releasing…" : "Release now"}
              </button>
            </div>
          ))}
        </div>
        {error && <div className="text-xs text-[#F4C7BC] mt-2" data-testid="bill-lock-banner-error">{error}</div>}
      </div>
    </div>
  );
}
